import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import React from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';
import getLocalePartialUrl from '@/utils/getLocalePartialUrl';

interface CaseStudy {
  title: string;
  summary: MessageKey;
  logo: string;
  learnMore: {
    url: string;
    label: MessageKey;
  };
}

const caseStudiesData = (localePartialUrl: string): CaseStudy[] => [
  {
    title: 'UC San Francisco Health',
    summary: 'pdp_horizon_research_case_studies_ucsf',
    logo: 'meet-the-community/logo-UCSF@2x',
    learnMore: {
      url: `https://ouraring.com/blog${localePartialUrl}early-covid-symptoms/`,
      label: 'pdp_horizon_case_studies_ucsf_cta',
    },
  },
  {
    title: 'UC San Diego',
    summary: 'pdp_horizon_research_case_studies_ucsd',
    logo: 'meet-the-community/logo-UCSD@2x',
    learnMore: {
      url: `https://ouraring.com/blog${localePartialUrl}temperature-increase-pregnancy/`,
      label: 'pdp_horizon_case_studies_ucsd_cta',
    },
  },
  {
    title: 'West Virginia University',
    summary: 'pdp_horizon_research_case_studies_west_virginia',
    logo: 'meet-the-community/logo-WVU@2x',
    learnMore: {
      url: `https://ouraring.com/blog${localePartialUrl}west-virginia-university-validates-oura/`,
      label: 'pdp_horizon_case_studies_wvu_cta',
    },
  },
];

const tValues = {
  i: (chunks: ReactNode) => <em className="font-serif italic">{chunks}</em>,
};

const Research = () => {
  const t = useTranslations();
  const locale = useLocale();
  const data = caseStudiesData(getLocalePartialUrl(locale));

  return (
    <section className="relative bg-sandstone-100 py-10 md:py-32">
      <LayoutGrid rowGap={32}>
        <Typography
          variant="h2"
          Element="h2"
          height="tight"
          className="col-main lg:col-end-13"
        >
          <RichText messageKey="pdp_horizon_research_title" tValues={tValues} />
        </Typography>
        <Typography
          variant="unset"
          className="col-main text-2xl lg:col-start-14 lg:text-3xl"
          weight="light"
          height="snug"
        >
          {t('pdp_horizon_research_description')}
        </Typography>
        <div className="col-main mt-12 lg:mt-16">
          <div className="grid grid-cols-1 lg:grid-flow-col lg:grid-cols-3 lg:gap-20">
            {data.map((caseStudy: CaseStudy) => (
              <div
                key={`casestudy-${caseStudy.title}`}
                className="flex flex-col items-start"
              >
                <div className="mb-8">
                  <Image
                    src={caseStudy.logo}
                    format="png"
                    className="h-16 w-auto"
                    alt={caseStudy.title}
                    loading="lazy"
                    width="330"
                    height="50"
                  />
                </div>
                <Typography className="mb-4">{t(caseStudy.summary)}</Typography>
              </div>
            ))}
          </div>
        </div>
      </LayoutGrid>
    </section>
  );
};

export default Research;
